import { useEffect, useMemo, useRef, useState } from 'react'
import { Loader2, MapPinned } from 'lucide-react'
import type { Map as MapboxMap, MapMouseEvent } from 'mapbox-gl'
import type { SurveyGeoCellDto } from '@/shared/api/types'
import { cn } from '@/lib/utils'
import { Note } from '@/shared/ui/states'
import { hasMapbox, loadMapbox, styleUrl, KENYA_BOUNDS } from '@/shared/map/mapbox'
import { count, pct } from './vocab'

/**
 * The aggregate layer: one circle per geography UNIT, at the unit's centroid.
 *
 * **Nothing on this layer is a respondent.** Every point is
 * `centroidLat`/`centroidLng` — the centroid of the county, constituency, ward
 * or polling-station catchment the interviews were attributed to — and a unit
 * holding fewer than `minCellBase` interviews never reaches the client at all.
 * The screen above says so in visible copy; this component is built so that it
 * *stays* true: it takes `SurveyGeoCellDto[]` and has no other data source, so
 * there is no coordinate here that could become a fix.
 *
 * `UnitMap` is the sibling component for one boundary or one marker, and this
 * follows its load / teardown / theme pattern deliberately. It does not reuse
 * it: `UnitMap` manages a single `mapboxgl.Marker`, and a Marker is a DOM node
 * per point — two hundred wards would be two hundred absolutely-positioned divs
 * repositioned on every frame of a pan. A GeoJSON source with one circle layer
 * is drawn on the GPU and is the right shape for "many".
 *
 * No satellite toggle, unlike `UnitMap`. Imagery is there to help somebody
 * recognise a building when they are placing a polling station; a centroid is a
 * derived point that corresponds to nothing on the ground, and putting it over a
 * rooftop would invite exactly the reading this screen exists to prevent.
 */

/**
 * A fixed, ordered, colour-vision-safe palette (Okabe–Ito, plus two of Tol's).
 *
 * Chart colour elsewhere in this app is single-hue on purpose — `shared/ui/charts`
 * encodes with length and position and never with hue, because the warm
 * `--chart-*` tokens are not tellable apart under CVD. A map cannot do that:
 * position is already spent on geography, so identity has nowhere to go but
 * hue, and these eight are separable under all three common CVD types. Hue is
 * still never the sole carrier — the legend below and the leading-option column
 * in the table both name the option in words.
 */
const PALETTE = [
  '#0072B2', '#D55E00', '#009E73', '#CC79A7',
  '#E69F00', '#56B4E9', '#882255', '#999933',
] as const

const slot = (i: number): string => PALETTE[i % PALETTE.length] ?? PALETTE[0]

/**
 * A cell with no leader. Ink, not a ninth category colour: "no estimate here"
 * is the absence of an answer, and giving it a hue of its own would put it in
 * the legend as a rival to the options that do have one.
 */
export const NO_LEADER_INK = '#94a3b8'

/**
 * Option id → colour, assigned by FIRST APPEARANCE over the cells sorted by
 * `unitId`.
 *
 * Not by rank, and the ordering is deliberately independent of `interviews`.
 * If colour tracked "who is leading in the most cells", every wave of a tracker
 * would repaint the whole map whenever two candidates swapped places — and a
 * reader comparing this month's map to last month's would read the recolouring
 * as a change of allegiance rather than a change of sort order. Keying off
 * `unitId` also means a late interview arriving, which changes counts but not
 * which units exist, does not move a single colour.
 *
 * A hash of the option id would be more stable still, but at a palette of eight
 * two options collide about as often as not, and two candidates rendered the
 * same colour is worse than a colour that shifts between waves. With more than
 * eight distinct leaders the palette wraps; the legend's labels are what tell
 * the repeats apart.
 */
export function optionColours(cells: SurveyGeoCellDto[]): Map<string, string> {
  const out = new Map<string, string>()
  const ordered = [...cells].sort((a, b) => a.unitId.localeCompare(b.unitId))
  for (const c of ordered) {
    if (c.leadingOptionId && !out.has(c.leadingOptionId)) {
      out.set(c.leadingOptionId, slot(out.size))
    }
  }
  return out
}

/** `centroidLat`/`centroidLng` arrive as BigDecimal strings. A cell may have neither. */
const coord = (v: string | number | null): number | null => {
  if (v === null || v === undefined || v === '') return null
  const n = typeof v === 'number' ? v : Number(v)
  return Number.isFinite(n) ? n : null
}

/**
 * The `unitId` a hit feature carries.
 *
 * Mapbox's `GeoJSONFeature` extends `GeoJSON.Feature` from `@types/geojson`,
 * which this project does not install — so `properties` is invisible to the
 * compiler although it is always there at runtime. One narrow cast, in one
 * place, rather than a dependency added for a single field.
 */
const unitIdOf = (feature: unknown): string | null => {
  const props = (feature as { properties?: Record<string, unknown> | null } | undefined)?.properties
  const id = props?.unitId
  return typeof id === 'string' ? id : null
}

const SOURCE = 'geo-cells'
const LAYER = 'geo-cells-circles'
const LAYER_SELECTED = 'geo-cells-selected'

/**
 * Radius in pixels for a cell holding `n` of a maximum `max` interviews.
 *
 * **Square root, so AREA carries the count.** A reader compares circles by the
 * ink in them, not by their width, so a radius proportional to `n` is read as
 * `n²` — a ward with four times the interviews of its neighbour looks sixteen
 * times as busy. The floor is not cosmetic either: the smallest cell that can
 * exist is `minCellBase` interviews, and it still has to be big enough to click.
 */
const radiusFor = (n: number, max: number): number => {
  const R_MIN = 5
  const R_MAX = 22
  if (max <= 0) return R_MIN
  return R_MIN + (R_MAX - R_MIN) * Math.sqrt(Math.max(n, 0) / max)
}

interface Hover {
  x: number
  y: number
  cell: SurveyGeoCellDto
}

export function GeoCellMap({
  cells,
  colours,
  selectedUnitId,
  onSelect,
  className,
}: {
  cells: SurveyGeoCellDto[]
  /** Shared with the table above so a swatch means the same thing in both. */
  colours: Map<string, string>
  selectedUnitId: string | null
  onSelect: (unitId: string) => void
  className?: string
}) {
  const host = useRef<HTMLDivElement>(null)
  const map = useRef<MapboxMap | null>(null)
  /** Latest props inside handlers registered once, without re-registering them. */
  const live = useRef({ cells, onSelect, selectedUnitId })
  live.current = { cells, onSelect, selectedUnitId }

  const [status, setStatus] = useState<'idle' | 'loading' | 'ready' | 'failed'>(
    hasMapbox() ? 'loading' : 'idle',
  )
  const [dark, setDark] = useState(() => document.documentElement.classList.contains('dark'))
  const [hover, setHover] = useState<Hover | null>(null)

  const plotted = useMemo(
    () => cells.filter((c) => coord(c.centroidLat) !== null && coord(c.centroidLng) !== null),
    [cells],
  )
  const unplottable = cells.length - plotted.length
  /** Refit only when the SET of units changes — not when a count ticks over. */
  const cellKey = useMemo(() => plotted.map((c) => c.unitId).join('|'), [plotted])

  /** The options actually present, in palette order, for the legend. */
  const legend = useMemo(() => {
    const seen = new Map<string, string>()
    for (const c of cells) {
      if (c.leadingOptionId && !seen.has(c.leadingOptionId)) {
        seen.set(c.leadingOptionId, c.leadingOptionLabel ?? c.leadingOptionId)
      }
    }
    return [...seen.entries()].map(([id, label]) => ({
      id, label, colour: colours.get(id) ?? NO_LEADER_INK,
    }))
  }, [cells, colours])
  const anyWithoutLeader = cells.some((c) => c.leadingOptionId === null)

  // Same MutationObserver as UnitMap: the theme is set once at boot, but a map
  // left white inside a dark shell is the failure mode, and this costs nothing.
  useEffect(() => {
    const el = document.documentElement
    const obs = new MutationObserver(() => setDark(el.classList.contains('dark')))
    obs.observe(el, { attributes: true, attributeFilter: ['class'] })
    return () => obs.disconnect()
  }, [])

  // ── create the map once ───────────────────────────────────────────────────
  useEffect(() => {
    if (!hasMapbox() || !host.current) return
    let cancelled = false
    setStatus('loading')

    loadMapbox().then((mapboxgl) => {
      if (cancelled || !host.current) return
      const m = new mapboxgl.Map({
        container: host.current,
        style: styleUrl('theme', document.documentElement.classList.contains('dark')),
        bounds: KENYA_BOUNDS,
        fitBoundsOptions: { padding: 24 },
        // Attribution is a Mapbox terms-of-service requirement, not decoration.
        cooperativeGestures: true,
      })
      m.addControl(new mapboxgl.NavigationControl({ showCompass: false }), 'top-right')
      m.addControl(new mapboxgl.ScaleControl({ unit: 'metric' }), 'bottom-left')
      let loaded = false
      m.on('load', () => { loaded = true; if (!cancelled) setStatus('ready') })
      m.on('error', () => {
        // Fatal only BEFORE the style has loaded. `error` also fires for a
        // missing sprite or one tile that 404s, and one of those flipping the
        // status permanently used to unmount the host div while this map went
        // on running — a rAF loop, a ResizeObserver and every handler still
        // bound to a container no longer in the document, with no path back.
        if (cancelled || loaded) return
        // Torn down here rather than in the cleanup, which only runs when the
        // component unmounts: entering the failed state removes the host div,
        // so the map has to go with it.
        m.remove()
        map.current = null
        setStatus('failed')
      })
      map.current = m
    }).catch(() => { if (!cancelled) setStatus('failed') })

    return () => {
      cancelled = true
      map.current?.remove()
      map.current = null
      setHover(null)
    }
    // Once. Style, data and selection are applied by the effects below;
    // recreating the map on a prop change would flash and lose the viewport.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ── theme ─────────────────────────────────────────────────────────────────
  useEffect(() => {
    const m = map.current
    if (!m || status === 'loading') return
    m.setStyle(styleUrl('theme', dark))
    // setStyle drops every source and layer; the data effect re-adds them on
    // `style.load`, which is why `draw` is registered there rather than run once.
  }, [dark, status])

  // ── the circles ───────────────────────────────────────────────────────────
  useEffect(() => {
    const m = map.current
    if (!m || status === 'failed') return

    const max = plotted.reduce((n, c) => Math.max(n, c.interviews), 0)
    const data = {
      type: 'FeatureCollection',
      features: plotted.map((c) => ({
        type: 'Feature',
        // Radius is precomputed rather than expressed as a Mapbox `interpolate`,
        // because the only interpolation Mapbox offers on a numeric input is
        // linear or exponential in the INPUT — neither of which is the square
        // root of the count, and a linear one would be the over-reading the
        // comment on `radiusFor` describes.
        properties: {
          unitId: c.unitId,
          radius: radiusFor(c.interviews, max),
          colour: c.leadingOptionId
            ? colours.get(c.leadingOptionId) ?? NO_LEADER_INK
            : NO_LEADER_INK,
          // A cell with no published leader is drawn thinner as well as greyer,
          // so it is still distinguishable in greyscale and under CVD.
          opacity: c.leadingOptionId ? 0.78 : 0.4,
        },
        geometry: {
          type: 'Point',
          coordinates: [coord(c.centroidLng), coord(c.centroidLat)],
        },
      })),
    }

    const draw = () => {
      const existing = m.getSource(SOURCE)
      if (existing) {
        // Cast as UnitMap does, to keep `@types/geojson` off this module's back.
        ;(existing as unknown as { setData: (d: unknown) => void }).setData(data)
        return
      }
      m.addSource(SOURCE, { type: 'geojson', data: data as never })
      m.addLayer({
        id: LAYER,
        type: 'circle',
        source: SOURCE,
        paint: {
          'circle-radius': ['get', 'radius'],
          'circle-color': ['get', 'colour'],
          'circle-opacity': ['get', 'opacity'],
          'circle-stroke-width': 1,
          'circle-stroke-color': dark ? '#0b0b0c' : '#ffffff',
        },
      })
      m.addLayer({
        id: LAYER_SELECTED,
        type: 'circle',
        source: SOURCE,
        // Selection is a second layer with a filter rather than a paint property
        // on the first: a filter is one `setFilter` call, where recolouring one
        // feature would mean rebuilding the whole FeatureCollection on every
        // click and losing the source's tiles for a frame. Seeded from the ref
        // rather than the prop because `setStyle` drops this layer and `draw`
        // rebuilds it — a highlight that vanished on a theme change would look
        // like the selection had been lost.
        filter: ['==', ['get', 'unitId'], live.current.selectedUnitId ?? ''],
        paint: {
          'circle-radius': ['get', 'radius'],
          'circle-color': 'rgba(0,0,0,0)',
          'circle-stroke-width': 3,
          'circle-stroke-color': '#7B3F00',
        },
      })
    }

    if (m.isStyleLoaded()) draw()
    m.on('style.load', draw)
    return () => { m.off('style.load', draw) }
  }, [plotted, colours, dark, status])

  // ── selection ─────────────────────────────────────────────────────────────
  useEffect(() => {
    const m = map.current
    if (!m || status !== 'ready' || !m.getLayer(LAYER_SELECTED)) return
    m.setFilter(LAYER_SELECTED, ['==', ['get', 'unitId'], selectedUnitId ?? ''])
  }, [selectedUnitId, status, cellKey])

  // ── hover and click ───────────────────────────────────────────────────────
  useEffect(() => {
    const m = map.current
    if (!m || status === 'failed') return

    const hitCell = (e: MapMouseEvent) => {
      const id = unitIdOf(e.features?.[0])
      return id === null ? null : live.current.cells.find((c) => c.unitId === id) ?? null
    }

    const onMove = (e: MapMouseEvent) => {
      const cell = hitCell(e)
      m.getCanvas().style.cursor = cell ? 'pointer' : ''
      setHover(cell ? { x: e.point.x, y: e.point.y, cell } : null)
    }
    const onLeave = () => {
      m.getCanvas().style.cursor = ''
      setHover(null)
    }
    const onClick = (e: MapMouseEvent) => {
      const cell = hitCell(e)
      if (cell) live.current.onSelect(cell.unitId)
    }

    m.on('mousemove', LAYER, onMove)
    m.on('mouseleave', LAYER, onLeave)
    m.on('click', LAYER, onClick)
    return () => {
      m.off('mousemove', LAYER, onMove)
      m.off('mouseleave', LAYER, onLeave)
      m.off('click', LAYER, onClick)
    }
  }, [status])

  // ── fit ───────────────────────────────────────────────────────────────────
  useEffect(() => {
    const m = map.current
    if (!m || status !== 'ready') return
    if (plotted.length === 0) {
      m.fitBounds(KENYA_BOUNDS, { padding: 24, duration: 0 })
      return
    }
    let minLng = 180, minLat = 90, maxLng = -180, maxLat = -90
    for (const c of plotted) {
      const lng = coord(c.centroidLng)
      const lat = coord(c.centroidLat)
      if (lng === null || lat === null) continue
      minLng = Math.min(minLng, lng); maxLng = Math.max(maxLng, lng)
      minLat = Math.min(minLat, lat); maxLat = Math.max(maxLat, lat)
    }
    // One cell has no extent to fit to, and `fitBounds` on a degenerate box
    // zooms to the maximum — a single county's centroid filling the screen at
    // street level, which reads as a precision the point does not have.
    if (minLng === maxLng && minLat === maxLat) {
      m.easeTo({ center: [minLng, minLat], zoom: 8, duration: 0 })
      return
    }
    m.fitBounds([[minLng, minLat], [maxLng, maxLat]], {
      padding: 56, duration: 0, maxZoom: 11,
    })
    // `cellKey` rather than `plotted`: a refit on every render would fight the
    // reader's pan, and the counts changing is not a reason to move the camera.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cellKey, status])

  const legendBlock = (
    <Legend rows={legend} withoutLeader={anyWithoutLeader} unplottable={unplottable} />
  )

  // ── no token, or the basemap did not load ─────────────────────────────────
  // Not a grey box. This is the path that runs in CI, offline in the field and
  // on the day a token is rotated badly, and the table below this component is
  // the whole dataset either way — so the honest thing is to say what is
  // missing, where it comes from, and that nothing has been withheld here.
  if (!hasMapbox() || status === 'failed') {
    return (
      <div className={cn('space-y-3', className)}>
        <Note tone="info">
          <span className="inline-flex items-start gap-2">
            <MapPinned className="h-4 w-4 mt-0.5 shrink-0" />
            <span>
              <strong className="font-medium text-foreground">
                {status === 'failed'
                  ? 'The basemap did not load.'
                  : 'No basemap is configured.'}
              </strong>{' '}
              {status === 'failed'
                ? 'A Mapbox token is set for this deployment but Mapbox could not be reached.'
                : 'Drawing a basemap needs a Mapbox access token. It is a per-deployment '
                  + 'value, deliberately empty by default: set AXIS_MAPBOX_TOKEN, which '
                  + 'reaches the browser as config.mapboxToken, and reload.'}{' '}
              {plotted.length > 0
                ? `All ${count(plotted.length)} cell${plotted.length === 1 ? '' : 's'} `
                : 'Every cell '}
              the server returned {plotted.length === 1 ? 'is' : 'are'} in the table below,
              in full. The map is a second view of these figures and never the only one —
              nothing is withheld by its absence.
            </span>
          </span>
        </Note>
        {legendBlock}
      </div>
    )
  }

  return (
    <div className={cn('space-y-3', className)}>
      <div className="relative border border-border rounded-md overflow-hidden h-[420px]">
        {/*
          The label is a sibling, and the container stays roleless. `role="img"`
          on it collapsed the whole subtree into one graphic — but Mapbox mounts
          the zoom buttons, the attribution links and a `tabindex="0"` canvas
          inside it, so those stayed in the tab order while being hidden from
          the accessibility tree. The table below is the text equivalent.
        */}
        <p className="sr-only">
          {`Map of ${plotted.length} geography units, sized by interview count. `}
          The same figures are in the table below.
        </p>
        <div ref={host} className="absolute inset-0" />

        {status === 'loading' && (
          <div className="absolute inset-0 grid place-items-center bg-muted/40
                          text-erp-xs text-muted-foreground">
            <span className="inline-flex items-center gap-2">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading map…
            </span>
          </div>
        )}

        {hover && (
          <div
            className="pointer-events-none absolute z-20 -translate-x-1/2 -translate-y-[calc(100%+12px)]
                       rounded-md border border-border bg-popover px-2 py-1.5 shadow-md
                       text-erp-xs text-popover-foreground whitespace-nowrap"
            style={{ left: hover.x, top: hover.y }}
          >
            <div className="font-medium">{hover.cell.unitName ?? 'Unnamed unit'}</div>
            <div className="tabular-nums text-muted-foreground">
              {count(hover.cell.interviews)} interviews
            </div>
            <div className="text-muted-foreground">
              {hover.cell.leadingOptionLabel
                ? `${hover.cell.leadingOptionLabel} · ${pct(hover.cell.leadingShare)}`
                : 'no leading option'}
            </div>
          </div>
        )}
      </div>

      {legendBlock}
    </div>
  )
}

function Legend({ rows, withoutLeader, unplottable }: {
  rows: { id: string; label: string; colour: string }[]
  withoutLeader: boolean
  unplottable: number
}) {
  if (rows.length === 0 && !withoutLeader && unplottable === 0) return null

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-erp-xs">
        {rows.map((r) => (
          <span key={r.id} className="inline-flex items-center gap-1.5">
            <span aria-hidden className="inline-block h-3 w-3 rounded-full border border-black/10"
              style={{ backgroundColor: r.colour }} />
            {r.label}
          </span>
        ))}
        {withoutLeader && (
          <span className="inline-flex items-center gap-1.5 text-muted-foreground">
            <span aria-hidden className="inline-block h-3 w-3 rounded-full border border-black/10 opacity-50"
              style={{ backgroundColor: NO_LEADER_INK }} />
            no leader — estimate withheld
          </span>
        )}
      </div>

      {rows.length === 0 && !withoutLeader && (
        // Only reachable with cells present, all of them plotted, none carrying a
        // leader and none lacking one — i.e. never in practice. Kept so the block
        // cannot render as a bare heading with nothing under it.
        <p className="text-erp-xs text-muted-foreground">Nothing is colour-coded on this view.</p>
      )}

      {unplottable > 0 && (
        <p className="text-erp-xs text-muted-foreground">
          {count(unplottable)} cell{unplottable === 1 ? ' has' : 's have'} no centroid on record
          and cannot be drawn. {unplottable === 1 ? 'It appears' : 'They appear'} in the table
          below with {unplottable === 1 ? 'its' : 'their'} full count — a unit that cannot be
          placed is not a unit that was not sampled.
        </p>
      )}
    </div>
  )
}

/**
 * The circle a table row wears, so the two views read as one.
 *
 * Exported here rather than duplicated in the page because a swatch that
 * disagreed with the map would be worse than no swatch at all.
 */
export function CellSwatch({ colour, muted }: { colour: string; muted?: boolean }) {
  return (
    <span
      aria-hidden
      className={cn(
        'inline-block h-2.5 w-2.5 rounded-full border border-black/10 shrink-0',
        muted && 'opacity-50',
      )}
      style={{ backgroundColor: colour }}
    />
  )
}
